<script setup lang="ts">
import { inject, ref, type Ref, watch } from 'vue';
import type { DynamicDialogInstance } from 'primevue/dynamicdialogoptions';
import CoreSpinner from '@/core/components/common/CoreSpinner.vue';
import { useToast } from 'primevue/usetoast';
import { useRoute } from 'vue-router';

const emit = defineEmits<{
  submit: [$event: Event];
  beforeClose: [$event: Event];
}>();

const props = withDefaults(
  defineProps<{
    title: string;
    footer?: boolean;
    onSave?: () => Promise<void | string>;
    cancelLabel?: string;
    submitLabel?: string;
    submitSeverity?: string;
    submitting?: boolean;
    class?: string;
  }>(),
  {
    title: '',
    footer: true,
    submitting: false,
    submitSeverity: '',
    submitLabel: 'Save',
    cancelLabel: 'Cancel',
  },
);

const toast = useToast();
const dialogRef = inject<Ref<DynamicDialogInstance>>('dialogRef');

const closing = ref(false);
const saving = ref(false);
const error = ref('');

const onSubmit = async ($event: Event) => {
  closing.value = true;
  saving.value = true;
  emit('submit', $event);
  if (props.onSave) {
    const err = await props.onSave();
    if (err) {
      toast.add({
        severity: 'error',
        summary: 'An error occured',
        detail: err,
        life: 5000,
      });
      $event.preventDefault();
      error.value = err;
      closing.value = false;
      saving.value = false;
      return;
    } else {
      if (dialogRef) {
        dialogRef.value.close(true);
      }
    }
  }

  if (!dialogRef) {
    return;
  }

  if (!$event.defaultPrevented) {
    $event.preventDefault();
    dialogRef.value.close(true);
  }

  closing.value = false;
  saving.value = false;
};
const onCloseClick = ($event: Event): void => {
  closing.value = true;
  emit('beforeClose', $event);

  if (!dialogRef) {
    return;
  }

  if (!$event.defaultPrevented) {
    dialogRef.value.close(null);
  }
  closing.value = false;
};
const route = useRoute();
watch(() => route, () => {
  // auto close dialog when url changes
  dialogRef?.value.close(null);
}, {deep:true})
</script>

<template>
  <form
    @submit="onSubmit($event)"
    class="flex min-h-32 w-full overflow-hidden"
    :class="props.class"
  >
    <div class="flex flex-col w-full overflow-hidden">
      <div
        class="flex flex-row justify-between items-center p-5"
        section="header"
      >
        <slot name="header">
          <div class="p-dialog-title">{{ props.title }}</div>
        </slot>
        <div class="p-dialog-header-actions">
          <Button
            class="p-button p-component p-button-icon-only p-button-secondary p-button-rounded p-button-text p-dialog-close-button"
            @click="onCloseClick($event)"
            type="button"
            label="Close"
            severity="secondary"
            :disabled="closing"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="p-icon p-button-icon"
              aria-hidden="true"
            >
              <path
                d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.3323 12.4999 2.23529C12.5016 2.13827 12.4838 2.0419 12.4474 1.95194C12.4111 1.86197 12.357 1.78024 12.2884 1.71163C12.2198 1.64302 12.138 1.58893 12.0481 1.55259C11.9581 1.51625 11.8617 1.4984 11.7647 1.50011C11.6677 1.50182 11.572 1.52306 11.4834 1.56255C11.3948 1.60204 11.315 1.65898 11.2488 1.72997L6.99067 5.98814L2.7325 1.72997C2.59553 1.60234 2.41437 1.53286 2.22718 1.53616C2.03999 1.53946 1.8614 1.61529 1.72901 1.74767C1.59663 1.88006 1.5208 2.05865 1.5175 2.24584C1.5142 2.43303 1.58368 2.61419 1.71131 2.75116L5.96948 7.00933L1.71131 11.2675C1.576 11.403 1.5 11.5866 1.5 11.7781C1.5 11.9696 1.576 12.1532 1.71131 12.2887C1.84679 12.424 2.03043 12.5 2.2219 12.5C2.41338 12.5 2.59702 12.424 2.7325 12.2887L6.99067 8.03052L11.2488 12.2887C11.3843 12.424 11.568 12.5 11.7594 12.5C11.9509 12.5 12.1346 12.424 12.27 12.2887C12.4053 12.1532 12.4813 11.9696 12.4813 11.7781C12.4813 11.5866 12.4053 11.403 12.27 11.2675L8.01186 7.00933Z"
                fill="currentColor"
              ></path>
            </svg>
            <span class="p-button-label" data-pc-section="label">&nbsp;</span
            ><!---->
          </Button>
        </div>
      </div>
      <div class="flex-1 min-h-0 flex relative overflow-hidden w-full">
        <div
          v-if="saving"
          class="absolute top-0 right-0 left-0 bottom-0 bg-white z-10 bg-opacity-60 pb-14 flex items-center justify-center"
        >
          <CoreSpinner></CoreSpinner>
        </div>

        <div class="overflow-y-auto p-5 pt-0 w-full">
          <slot></slot>
        </div>
      </div>
      <div class="flex-initial h-24 p-5" v-if="props.footer">
        <slot name="footer">
          <div class="flex flex-row justify-end">
            <Button
              class="mr-4 min-w-24"
              type="button"
              v-if="!!cancelLabel"
              :label="cancelLabel"
              severity="secondary"
              @click="onCloseClick($event)"
              :disabled="closing"
            ></Button>
            <Button
              class="min-w-24"
              type="submit"
              :severity="submitSeverity"
              :loading="submitting"
              v-if="!!submitLabel"
              :label="submitLabel"
              :disabled="closing"
            />
          </div>
        </slot>
      </div>
    </div>
  </form>
</template>

<style scoped>
.change-colors {
  --color: var(--p-primary-contrast-color);
  --p-progressspinner-color-one: var(--color);
  --p-progressspinner-color-two: var(--color);
  --p-progressspinner-color-three: var(--color);
  --p-progressspinner-color-four: var(--color);
  color: var(--color);
}
</style>
